/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useState } from 'react';

import Cropper from 'react-easy-crop';
import { getCroppedImg } from '@client/logic/cropImage';
import { debounce } from 'lodash';
import { faXmark, faCrop } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { PlainModal } from '@design-system/components/modal/modal';
import { AnyFn } from '../helpers/types';
import * as styles from './CropModal.css';

type Props = {
  isOpen: boolean;
  onCancel: () => void;
  onSave: () => void;
  aspectRatio: number | undefined;
  value?: string;
  onChange: (image: string) => void;
  disabled?: boolean;
};

export default function CropModal({
  isOpen,
  onCancel,
  onSave,
  aspectRatio,
  value,
  onChange,
  disabled = false,
}: Props) {
  return (
    <PlainModal
      isOpen={isOpen}
      onClose={onCancel}
      modalClassName={styles.modalOverride}
    >
      <div className={styles.header}>
        <span className={styles.title}>
          <Icon icon={faCrop} />
          Crop image
        </span>
        <button
          type="button"
          className={styles.closeButton}
          onClick={onCancel}
          aria-label="Close"
        >
          <Icon icon={faXmark} />
        </button>
      </div>

      <div className={styles.cropArea}>
        {value && aspectRatio && (
          <CropImage src={value} onChange={onChange} aspect={aspectRatio} />
        )}
      </div>

      <div className={styles.footer}>
        <span className={styles.hint}>Drag to reposition, scroll to zoom</span>
        <div className={styles.footerActions}>
          <button
            type="button"
            className={styles.cancelButton}
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className={styles.uploadButton}
            onClick={onSave}
            disabled={disabled}
          >
            Apply crop
          </button>
        </div>
      </div>
    </PlainModal>
  );
}

function useEasyCrop({
  src,
  aspect = 1,
  onChange,
}: {
  onChange: AnyFn;
  aspect?: number;
  src?: any;
}): Cropper['props'] {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [rotation, setRotation] = useState(0);
  const [zoom, setZoom] = useState(1);

  const onChangeHandlerDebounced = useCallback(
    debounce(async (croppedAreaPixels) => {
      const croppedImage = await getCroppedImg(
        src,
        croppedAreaPixels,
        rotation,
      );
      onChange(croppedImage);
    }, 200),
    [],
  );

  const onCropComplete = useCallback(
    async (_croppedArea, croppedAreaPixels) => {
      onChange(null);
      onChangeHandlerDebounced(croppedAreaPixels);
    },
    [],
  );

  // @ts-ignore
  return {
    image: src,
    crop,
    rotation,
    onCropChange: setCrop,
    onCropComplete,
    onRotationChange: setRotation,
    aspect,
    zoomWithScroll: true,
    onZoomChange: setZoom,
    zoom,
  };
}

type CropImageProps = {
  src: string;
  aspect: number;
  onChange: (image: string) => void;
};

function CropImage({ src, aspect = 1, onChange }: CropImageProps) {
  const cropper = useEasyCrop({ src, aspect, onChange });

  return (
    <div>
      <Cropper {...cropper} />
    </div>
  );
}
